import { getDocs } from "firebase/firestore";
import { businessCollection } from "./firestorePaths";

// Products, categories and stock all come out of the same two collections, so
// they live together: businesses/{id}/products and businesses/{id}/categories.
//
// The stored product shape is the POS's deeply nested `prod*` model. The tables
// expect a flat shape, so the mapping happens here and the views stay unchanged.

const str = (v) => (typeof v === "string" ? v.trim() : "");
const num = (v, fallback = 0) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  // Stock movements store amounts as strings.
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
};

/** prodInventory.quantity is {total, byLocation[]} - never a bare number. */
function stockLevel(inventory = {}) {
  const q = inventory.quantity;
  if (typeof q === "number") return q;
  if (q && typeof q === "object") {
    if (typeof q.total === "number") return q.total;
    if (Array.isArray(q.byLocation)) {
      return q.byLocation.reduce((acc, l) => acc + num(l.available), 0);
    }
  }
  return 0;
}

function productStatus(data = {}) {
  const meta = data.prodMetadata || {};
  if (meta.archivedAt) return "archived";
  if (data.prodOnline?.visible) return "active";
  return "draft";
}

function productImages(media = {}) {
  const images = Array.isArray(media.images) ? media.images : [];
  return images
    .slice()
    .sort((a, b) => num(a.sort) - num(b.sort))
    .map((img) => ({
      url: str(img.url),
      alt: str(img.altText),
      isPrimary: Boolean(img.primary),
    }));
}

function normalizeProduct(id, data = {}, categoryNames = {}) {
  const pricing = data.prodPricing || {};
  const inventory = data.prodInventory || {};
  const categoryIds = Array.isArray(data.prodCategories) ? data.prodCategories : [];

  return {
    productId: str(data.prodId) || id,
    productName: str(data.prodName),
    // `short` and `long` hold the same text in practice; prefer short.
    productDescription: str(data.prodDescription?.short) || str(data.prodDescription?.long),
    productStatus: productStatus(data),
    productImages: productImages(data.prodMedia),
    // The table renders a single category string; keep the ids for filtering.
    productCategory: categoryIds.map((cid) => categoryNames[cid] || cid).join(", "),
    productCategoryIds: categoryIds,
    // currentPrice is 0 on every product in practice - basePrice is where the
    // real figure lives. A plain fallback would not help: 0 is a valid number,
    // so it has to be an explicit > 0 check.
    productRetailPrice: num(pricing.currentPrice) > 0
      ? num(pricing.currentPrice)
      : num(pricing.basePrice),
    productCompareAtPrice: num(pricing.msrp) > 0 ? num(pricing.msrp) : 0,
    productCostPrice: num(pricing.costPrice),
    productCurrency: str(pricing.currency),
    productSku: str(data.prodSku),
    productBarcode: str(data.prodBarcode) || str(data.prodPhysical?.barcode),
    productUnit: str(data.prodUnit),
    productType: str(data.prodType),
    trackInventory: Boolean(data.prodManage),
    inventoryQuantity: stockLevel(inventory),
    lowStockThreshold: num(inventory.lowStockThreshold),
    inventoryByLocation: Array.isArray(inventory.quantity?.byLocation)
      ? inventory.quantity.byLocation
      : [],
    supplier: {
      id: str(data.prodSupplier?.id),
      name: str(data.prodSupplier?.name),
      email: str(data.prodSupplier?.contactInfo?.email),
      phone: str(data.prodSupplier?.contactInfo?.phone),
      website: str(data.prodSupplier?.contactInfo?.website),
      leadTime: num(data.prodSupplier?.leadTime),
      minimumOrderQuantity: num(data.prodSupplier?.minimumOrderQuantity),
      preferred: Boolean(data.prodSupplier?.preferredSupplier),
    },
    levies: Array.isArray(pricing.levies) ? pricing.levies : [],
    // The POS has no variant data on these documents yet.
    options: Array.isArray(data.prodOptions) ? data.prodOptions : [],
    variants: Array.isArray(data.prodVariations) ? data.prodVariations : [],
    createdAt: num(data.prodMetadata?.createdAt),
    updatedAt: num(data.prodMetadata?.updatedAt),
  };
}

function normalizeCategory(id, data = {}) {
  return {
    categoryId: str(data.categoryId) || id,
    categoryName: str(data.categoryName),
    description: str(data.description),
    online: Boolean(data.online),
    highlighted: Boolean(data.highlighted),
    image: str(data.alternateImageUrl) || str(data.bannerImageUrl),
    parentIds: Array.isArray(data.parentIds) ? data.parentIds : [],
    locations: Array.isArray(data.locations) ? data.locations : [],
  };
}

export async function fetchCategories(businessId) {
  if (!businessId) return [];
  const snap = await getDocs(businessCollection(businessId, "categories"));
  return snap.docs.map((d) => normalizeCategory(d.id, d.data()));
}

export async function fetchProducts(businessId) {
  if (!businessId) return [];

  // Categories are needed to turn category ids into names on each row.
  const [categories, snap] = await Promise.all([
    fetchCategories(businessId),
    getDocs(businessCollection(businessId, "products")),
  ]);

  const names = {};
  categories.forEach((c) => {
    names[c.categoryId] = c.categoryName;
  });

  return snap.docs.map((d) => normalizeProduct(d.id, d.data(), names));
}

export async function fetchProductById(businessId, id) {
  if (!businessId || !id) return null;
  const products = await fetchProducts(businessId);
  return products.find((p) => p.productId === id) ?? null;
}

/**
 * Stock rows for the Inventory tab.
 *
 * Levels come from the product, movement history from the separate `movements`
 * collection which stores one document per product with an array of entries.
 */
export async function fetchStock(businessId) {
  if (!businessId) return [];

  const [products, movementsSnap] = await Promise.all([
    fetchProducts(businessId),
    getDocs(businessCollection(businessId, "movements")),
  ]);

  const movementsByProduct = {};
  movementsSnap.forEach((d) => {
    const data = d.data() || {};
    movementsByProduct[str(data.productId) || d.id] = (
      Array.isArray(data.movements) ? data.movements : []
    ).map((m) => ({
      type: str(m.type),
      amount: num(m.amount),
      current: num(m.current),
      cost: num(m.cost),
      date: num(m.date),
      location: str(m.location),
      staff: str(m.staff),
    }));
  });

  return products.map((p) => {
    const movements = movementsByProduct[p.productId] || [];
    const onHand = p.inventoryQuantity;
    const threshold = p.lowStockThreshold;
    const locations = p.inventoryByLocation;

    // `reserved` is what a location has committed but not yet released.
    const committed = locations.reduce((acc, l) => acc + num(l.reserved), 0);

    return {
      id: p.productId,
      productId: p.productId,
      productName: p.productName,
      productImages: p.productImages,
      sku: p.productSku,
      category: p.productCategory,
      // A product can sit in several locations; the row shows the first.
      location: str(locations[0]?.locationId),
      trackInventory: p.trackInventory,
      onHand,
      committed,
      // Field names deriveStockStatus() and the table columns read.
      available: Math.max(0, onHand - committed),
      reorderPoint: threshold,
      lowStockThreshold: threshold,
      retailPrice: p.productRetailPrice,
      costPrice: p.productCostPrice,
      byLocation: locations,
      movements,
      lastMovement: movements.length
        ? movements.reduce((a, b) => (a.date > b.date ? a : b))
        : null,
      value: onHand * p.productCostPrice,
    };
  });
}
